import React, { useEffect, useState } from 'react'
import {
  useRecoilCallback,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
  useRecoilState
} from 'recoil'
import { useIntl } from 'react-intl'
import evalCondition from 'Helpers/evalCondition'

import {
  Panel,
  Button,
  BUTTON_VARIANT_SECONDARY,
  Link,
  LINK_VARIANT_EXTRALIGHT,
  TooltipUtils,
  toastDismiss,
  SpacingVertical,
  SPACING_16,
  Flex,
  FlexItem,
  Title,
  TITLE_VARIANT_M,
  Text,
  TEXT_VARIANT_LIGHT,
  HelpIcon
} from 'mailup-web-components'
import DataTablePreview from './DataTablePreview'
import BoxResultIcon from './icons/BoxResultIcon'

import {
  abTestState,
  recipientsDataTablePreviewState,
  searchCriteriaState,
  sendingPreviewState
} from 'Pages/AbTestWizard/recoil/state/abtest'
import {
  sendingPreviewCallback,
  setTestGroupCallback,
  searchRecipientsAndSendingPreviewDataCallback
} from 'Pages/AbTestWizard/recoil/callbacks/callbacks'
import { modalColumnsManagementState } from 'Pages/AbTestWizard/recoil/state/modals'

import {
  RECIPIENTS_ALL,
  RECIPIENTS_GROUP_FILTER,
  RECIPIENTS_TEST_GROUP
} from 'Pages/AbTestWizard/const'

const BoxResults = () => {
  const intl = useIntl()

  const { step2, step3 } = useRecoilValue(abTestState)
  const [
    { toBeQueued, totalRecipients },
    setSendingPreviewState
  ] = useRecoilState(sendingPreviewState)
  const { loaded } = useRecoilValue(searchCriteriaState)
  const { items } = useRecoilValue(recipientsDataTablePreviewState)

  const resetRecipientsDataTablePreviewState = useResetRecoilState(
    recipientsDataTablePreviewState
  )
  const resetSendingPreviewState = useResetRecoilState(sendingPreviewState)

  const setModalColumnState = useSetRecoilState(modalColumnsManagementState)

  const [btnPreviewText, setBtnPreviewText] = useState(
    'Load.EmailSendingWizard.ShowPreviewNew'
  )
  const [recipientStrVisible, setRecipientStrVisible] = useState(false)

  const sendingPreview = useRecoilCallback(sendingPreviewCallback, [])
  const setTestGroup = useRecoilCallback(setTestGroupCallback, [])
  const searchRecipientsAndSendingPreviewData = useRecoilCallback(
    searchRecipientsAndSendingPreviewDataCallback,
    []
  )

  useEffect(() => {
    if (loaded) {
      // vuol dire che sono stati caricati i gruppi/filtri, in ALL non esiste il loading

      if (step2.recipients === RECIPIENTS_TEST_GROUP) {
        setTestGroup().then(e => {
          setTimeout(() => {
            setRecipientStrVisible(true)
            setBtnPreviewText('Load.EmailSendingWizard.RechargePreview')
            searchRecipientsAndSendingPreviewData()
          })
        })
      } else {
        // prendo solo i recipients
        if (
          step3.includeGroups.length > 0 ||
          step3.excludeGroups.length > 0 ||
          step3.selectedFilters.length > 0
        ) {
          handleBtnPreviewClick() // se c'è una precedente selezione
        } else {
          sendingPreview() // prima volta o non ci sono selezioni
        }
      }
    }
  }, [loaded])

  useEffect(() => {
    setSendingPreviewState(prev => ({
      ...prev,
      showLoading: true
    }))

    step2.recipients === RECIPIENTS_ALL &&
      sendingPreview().then(e => {
        setRecipientStrVisible(true)
      })

    return () => {
      setRecipientStrVisible(false)
      resetRecipientsDataTablePreviewState()
      resetSendingPreviewState()
    }
  }, [])

  useEffect(() => {
    if (
      step3.includeGroups.length > 0 ||
      step3.excludeGroups.length > 0 ||
      step3.selectedFilters.length > 0
    ) {
      toastDismiss('LoadEmailSendingWizardSelectRecipError')
      toastDismiss('LoadEmailSendingWizarderrSendLimitReached')
      toastDismiss('tooLittlePoolABTest')
    }
  }, [step3.includeGroups, step3.excludeGroups, step3.selectedFilters])

  const handleBtnPreviewClick = () => {
    setBtnPreviewText('Load.EmailSendingWizard.RechargePreview')
    setSendingPreviewState(prev => ({
      ...prev,
      showLoading: true
    }))
    searchRecipientsAndSendingPreviewData()
    toastDismiss('LoadEmailSendingWizardSelectRecipError')
    toastDismiss('LoadEmailSendingWizarderrSendLimitReached')
    toastDismiss('tooLittlePoolABTest')
  }

  const handleClickSetColumns = () => {
    setModalColumnState(prev => ({
      ...prev,
      show: true
    }))
  }

  const BtnPreview = () => {
    const isDisabed = () => {
      return (
        !(
          step3.includeGroups.length > 0 ||
          step3.excludeGroups.length > 0 ||
          step3.selectedFilters.length > 0
        ) && step2.recipients === RECIPIENTS_GROUP_FILTER
      )
    }
    return (
      <FlexItem flex='1'>
        <Button
          kind={BUTTON_VARIANT_SECONDARY}
          onClick={handleBtnPreviewClick}
          disabled={isDisabed()}
          data-mu-test='abtestWizardStep3__button-preview-recipients'
        >
          {intl.formatMessage({
            id: btnPreviewText
          })}
        </Button>
      </FlexItem>
    )
  }

  const Preview = () => {
    return !evalCondition('uo:PRIVACYBLOCK') ? (
      <>
        {items && (
          <Title variant={TITLE_VARIANT_M}>
            {intl.formatMessage({
              id: 'Load.SearchRecipients.ResultsDescriptionNew'
            })}
          </Title>
        )}

        <DataTablePreview />

        {/* imposta le colonne solo se ci sono risultati, se non ci sono risultati non si vede */}
        {items && items.length > 0 && (
          <Flex
            flexDirection='column'
            alignItems='flex-end'
            width='100%'
            margin='4px 0 0 0'
          >
            <Link
              onClick={handleClickSetColumns}
              variant={LINK_VARIANT_EXTRALIGHT}
            >
              {intl.formatMessage({
                id: 'Load.SearchRecipients.RecipientHeadingsTitle'
              })}
            </Link>
          </Flex>
        )}
      </>
    ) : (
      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage(
          {
            id: 'Load.SearchRecipients.PrivacyBlockedNew'
          },
          {
            strong: chunks => <strong>{chunks}</strong>,
            linebreak: <br />
          }
        )}
      </Title>
    )
  }
  useEffect(() => {
    if (
      step2.recipients === RECIPIENTS_ALL ||
      ((step3.includeGroups.length > 0 ||
        step3.excludeGroups.length > 0 ||
        step3.selectedFilters.length > 0) &&
        toBeQueued)
    ) {
      setRecipientStrVisible(true)
    } else {
      setRecipientStrVisible(false)
    }
  }, [toBeQueued])

  const Recipients = () => {
    return (
      <Flex width='100%'>
        <FlexItem margin='0 0 0 32px'>
          <Title>
            <Flex>
              {!recipientStrVisible && (
                <FlexItem margin='0 4px 0 0'>- </FlexItem>
              )}
              {recipientStrVisible && toBeQueued === undefined && (
                <FlexItem margin='0 4px 0 0'>- </FlexItem>
              )}
              {recipientStrVisible &&
                toBeQueued > 0 &&
                `${intl.formatNumber(toBeQueued)} `}
              {recipientStrVisible && toBeQueued <= 0 && '0 '}
              {intl.formatMessage({
                id: 'Load.SearchRecipients.PreviewTotalSubscribersNew'
              })}
            </Flex>
          </Title>
        </FlexItem>
      </Flex>
    )
  }

  const Selectable = () => {
    useEffect(() => {
      TooltipUtils.rebuild()
    }, [])

    return (
      <Flex width='100%'>
        <FlexItem margin='0 0 0 32px'>
          <Text variant={TEXT_VARIANT_LIGHT}>
            <Flex alignItems='center'>
              {totalRecipients === undefined && (
                <FlexItem margin='0 4px 0 0'>/ -</FlexItem>
              )}
              {totalRecipients > 0 &&
                `/ ${intl.formatNumber(totalRecipients)} `}
              {totalRecipients <= 0 && '/ 0 '}
              {intl.formatMessage({
                id: 'Load.SearchRecipients.PreviewTotalSelectableNew'
              })}
              <HelpIcon
                text={intl.formatMessage({
                  id:
                    'Load.EmailSendingWizard.TotalSelectableSubscribersHelpNew'
                })}
              />
            </Flex>
          </Text>
        </FlexItem>
      </Flex>
    )
  }

  return (
    <>
      <Panel isClickable={false} collapsible={false}>
        <Flex justifyContent='space-between' alignItems='center'>
          <BoxResultIcon />
          <FlexItem flex='1'>
            <Recipients />
            <Selectable />
          </FlexItem>
          {step2.recipients !== RECIPIENTS_ALL && <BtnPreview />}
        </Flex>
        {step2.recipients !== RECIPIENTS_ALL && (
          <>
            <SpacingVertical variant={SPACING_16} />
            <Preview />
          </>
        )}
      </Panel>
    </>
  )
}

export default BoxResults
